// assets/AllCourses/Exercises/ListeningExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { StringField } from "@/components/shared/FormFields";
import SpeakerButton from "@/components/ui/SpeakerButton";
import { cn } from "@/utils/utils";

export default function ListeningExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.listeningQuestions)
    ? exercise.listeningQuestions.map(q => ({
        wordListening:
          typeof q.wordListening === "string"
            ? q.wordListening.trim()
            : "",
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    answers: {},
    hasInteracted: false,
  });

  const completedRef = useRef(false);
useEffect(() => {
  if (!showResults) completedRef.current = false;
}, [showResults]);
  /* ================= SPEECH SANITIZER ================= */
const sanitizeForSpeech = (text = "") => {
  return String(text)
    // 2 or more underscores → "blank"
    .replace(/_{2,}/g, " blank ")
    // single underscore → "underscore"
    .replace(/_/g, " underscore ")
    // clean extra spaces
    .replace(/\s+/g, " ")
    .trim();
};


  /* ================= RESTORE ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;

    setState({
      answers:
        typeof exercise.attempt.answers === "object"
          ? exercise.attempt.answers
          : {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  }, [isPreview, exercise.attempt]);

  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "") =>
    String(text || "")
      .trim()
      .toLowerCase()
      .replace(/[.,!?]/g, "")
      .replace(/\s+/g, " ");

  /* ================= EDIT HELPERS ================= */
  const updateQuestions = next => {
    if (isEditMode) onUpdate("listeningQuestions", next);
  };

  const updateWord = (idx, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[idx] = {
      wordListening:
        typeof value === "string" ? value.trim() : "",
    };

    updateQuestions(next);
  };

  /* ================= STUDENT INPUT ================= */
  const handleInputChange = (idx, value) => {
    if (isEditMode || showResults || completedRef.current)
      return;

    setState(prev => {
      const answers = { ...prev.answers, [idx]: value };
      const next = {
        answers,
        hasInteracted: true,
      };

      setAttempt(next);

      const allAnswered = questions.every(
        (_, i) => answers[i]?.trim()
      );

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every(
          (q, i) =>
            normalize(answers[i]) ===
            normalize(q.wordListening)
        );

        onComplete(exercise._id, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6">
            <div className="space-y-4">
              <StringField
                label={`Word / Sentence ${idx + 1}`}
                value={q.wordListening}
                onChange={v => updateWord(idx, v)}
                placeholder="apple / Good morning, how are you?"
                required
                error={
                  errors?.[
                    `exercise-${exercise?.index ?? idx}-listening-${idx}`
                  ]
                }
              />

              {q.wordListening && (
                <div className="flex items-center gap-3">
<SpeakerButton text={sanitizeForSpeech(q.wordListening)} />
                  <span className="text-sm text-muted-foreground">
                    Preview pronunciation
                  </span>
                </div>
              )}

              <Button
                size="sm"
                variant="destructive"
                disabled={questions.length === 1}
                onClick={() =>
                  updateQuestions(
                    questions.filter((_, i) => i !== idx)
                  )
                }
              >
                Remove
              </Button>
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              { wordListening: "" },
            ])
          }
        >
          Add Word / Sentence
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No listening questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Listening Practice
        </CardTitle>
        <CardDescription>
          Listen carefully and type exactly what you hear.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {questions.map((q, idx) => {
          const userAnswer = state.answers[idx] || "";
          const isCorrect =
            userAnswer &&
            normalize(userAnswer) ===
              normalize(q.wordListening);

          return (
            <div
              key={idx}
              className="rounded-xl border bg-muted/40 p-6 space-y-4"
            >
              <div className="flex items-center gap-4">
               <SpeakerButton
                text={sanitizeForSpeech(q.wordListening)}
                size="lg"
              />

                <span className="text-sm text-muted-foreground">
                  Click to listen, then type below
                </span>
              </div>

              <Textarea
              rows={3}
              value={userAnswer}
              placeholder="Type what you hear..."
              onChange={e =>
                handleInputChange(idx, e.target.value)
              }
              disabled={showResults || completedRef.current}
              className={cn(
                "text-base transition border",
                showResults && userAnswer && isCorrect &&
                  "bg-green-100 border-green-300",
                showResults && userAnswer && !isCorrect &&
                  "bg-red-100 border-red-300",
                showResults && "cursor-not-allowed opacity-95"
              )}
            />

              {showResults && userAnswer && (
                <p
                  className={cn(
                    "text-sm font-medium",
                    isCorrect
                      ? "text-green-600"
                      : "text-red-600"
                  )}
                >
                  {isCorrect
                    ? "Correct"
                    : `Correct answer: "${q.wordListening}"`}
                </p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
